/* The scenario catalogue, as the browser sees it (#94).
 *
 * `src/engine/scenario.js` owns the format, the builder and the goal checker.
 * This is the thin layer above it: fetch the file, validate what came back,
 * and remember which puzzle the player is on. The engine half is pure and runs
 * in Node for `npm run scenarios`; this half only ever runs in a browser.
 *
 * ---------------------------------------------------------------------------
 * Why the URL is a parameter
 *
 * Scenarios are the one thing on the supporter list that does not need to move
 * server-side to be a real gate — the *files* are the product, and not sending
 * them is a genuine gate rather than a hidden button (#94, and the table in
 * CLAUDE.md). Today they are a static file copied into `dist/` by
 * `vite.config.mjs`; the day there is an API, the only thing that changes is
 * this URL, and everything below it — normalising, building, scoring — is
 * already indifferent to where the JSON came from.
 *
 * Nothing here imports `src/entitlement.js`, and nothing here should: that
 * module is server-side only and `test/entitlement.test.mjs` fails if anything
 * under `src/ui`, `src/data` or `src/engine` reaches for it. The client does
 * not decide who is a supporter — it asks for a file and is given one or not.
 */

import { normalizeScenario, scenarioCardIds } from '../engine/scenario.js';

let catalogue = null;      // normalised, in file order
let rejected = [];         // entries that failed validation, for the console
let loadError = null;
let inflight = null;

/**
 * Fetch and validate the catalogue. Idempotent — concurrent callers share one
 * request, and a second call after success is free.
 *
 * A malformed entry is skipped rather than thrown, which is the opposite of
 * what `normalizeScenario` does on its own and deliberate: `npm run scenarios`
 * gates CI, so a bad puzzle cannot reach a user — but if one ever did, losing
 * that puzzle is better than losing the tab.
 */
export function loadScenarios(url = './data/scenarios.json') {
  if (catalogue) return Promise.resolve(catalogue);
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const raw = await r.json();
      const list = Array.isArray(raw?.scenarios) ? raw.scenarios : [];

      const out = [];
      const bad = [];
      const seen = new Set();
      for (const entry of list) {
        try {
          const sc = normalizeScenario(entry);
          if (seen.has(sc.id)) throw new TypeError(`duplicate id ${sc.id}`);
          seen.add(sc.id);
          out.push(sc);
        } catch (err) {
          bad.push(`${entry?.id ?? '(no id)'}: ${err.message}`);
        }
      }
      if (bad.length) console.warn(`Skipped ${bad.length} malformed scenario(s):\n  ${bad.join('\n  ')}`);

      catalogue = out;
      rejected = bad;
      loadError = null;
      return catalogue;
    } catch (err) {
      /* An empty list, not a throw. The Playtest tab has a whole free-play
       * half that has nothing to do with scenarios, and it must still open.
       *
       * `catalogue` is deliberately left null: a failure is not an answer, and
       * caching one would turn a dropped connection into a tab that stays
       * empty for the rest of the session. */
      loadError = String(err?.message || err);
      return [];
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export const getScenarios = () => catalogue || [];
export const scenariosError = () => loadError;
export const scenariosRejected = () => rejected;
export const scenarioById = id => getScenarios().find(s => s.id === id) || null;

/**
 * Which card ids this puzzle needs that the database does not have.
 *
 * A scenario is never played against stub bodies. `withStubs` exists so a
 * missing printing cannot abort a *game*, where a vanilla stand-in costs
 * nothing but accuracy — but a puzzle's answer is computed from printed text,
 * and a stubbed card turns a proven line into an unsolvable one. So the puzzle
 * is withheld instead, which is a thing we can explain.
 */
export const missingCards = (sc, cards) => scenarioCardIds(sc).filter(id => !cards[id]);

/* ---------------------------------------------------------------------- */
/* progress                                                                */
/* ---------------------------------------------------------------------- */

/* Browser-local and deliberately so. Real progress — solved, attempts, across
 * devices — is a table, and waits on the same account infrastructure as
 * everything else in #40. Until then this is enough to tick off a puzzle you
 * have already beaten, and it costs nothing to throw away when the table
 * lands: the id is the only thing recorded, and the server can only ever know
 * more than this does. */
const SOLVED_KEY = 'optcg.scenarios.solved';
const CURRENT_KEY = 'optcg.scenarios.current';

function readJson(key, fallback) {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || 'null');
    return raw ?? fallback;
  } catch { return fallback; }
}

function write(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch { /* private mode / quota — progress is a nicety, not the feature */ }
}

export function solvedIds() {
  const raw = readJson(SOLVED_KEY, []);
  return Array.isArray(raw) ? raw.filter(v => typeof v === 'string') : [];
}

export const isSolved = id => solvedIds().includes(id);

export function markSolved(id) {
  const all = solvedIds();
  if (all.includes(id)) return;
  write(SOLVED_KEY, [...all, id]);
}

/* Which puzzle the player is in the middle of, so it survives a reload.
 *
 * Only the id is kept, never the position. Replaying the moves back would need
 * the opponent's answers to come out the same way twice, and they are the AI's
 * — so a restored board could differ from the one that was on screen, which is
 * a worse lie than starting over. Retry is free and unlimited by design, so
 * "you are still on this puzzle, from the top" is an honest restore.
 *
 * Cleared on solving and on leaving, so a finished puzzle does not keep
 * capturing the Playtest tab on every future visit. */
export const currentScenarioId = () => {
  const raw = readJson(CURRENT_KEY, null);
  return typeof raw === 'string' ? raw : null;
};

export const setCurrentScenarioId = id => write(CURRENT_KEY, id || null);
